import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { UsersRound, Search, ChevronLeft, ChevronRight, Loader2, AlertCircle, RefreshCw, Upload, CheckSquare, Square, FileSpreadsheet } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { functions, db } from '../../lib/firebase';

// ---------- Types ----------
interface EligibleClient {
  customerName: string;
  loanCount: number;
  totalTrailCents: number;
  totalUpfrontCents: number;
  latestPeriodMonth: string;
  officeId: string;
  officeName: string;
  brokerId: string;
  brokerDisplay: string;
  isEnrolled: boolean;
}

interface OfficeOption {
  id: string;
  name: string;
}

interface CsvRow {
  customerName: string;
  email: string;
}

const PAGE_SIZE = 25;

const BulkActivationPage: React.FC = () => {
  const { companyId } = useAuth();

  // Data state
  const [clients, setClients] = useState<EligibleClient[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Office/broker data
  const [allOffices, setAllOffices] = useState<OfficeOption[]>([]);

  // Filter state
  const [search, setSearch] = useState('');
  const [officeFilter, setOfficeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [hideEnrolled, setHideEnrolled] = useState(true);

  // Selection state
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // CSV state
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Invite state
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: string; text: string } | null>(null);
  const [backdatePeriod, setBackdatePeriod] = useState<'0' | '3' | '6' | '12' | 'fy' | 'all'>('3');

  // Fetch offices
  useEffect(() => {
    if (!companyId) return;
    getDocs(collection(db, `companies/${companyId}/offices`)).then((snap) => {
      const offices = snap.docs
        .map((d) => ({ id: d.id, name: (d.data().name as string) || 'Unnamed' }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllOffices(offices);
    });
  }, [companyId]);

  // Fetch eligible clients
  const fetchClients = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<
        { companyId: string; officeId?: string; search?: string; limit?: number; offset?: number },
        { clients: EligibleClient[]; total: number }
      >(functions, 'getLoyaltyEligibleClients');

      const result = await fn({
        companyId,
        officeId: officeFilter !== 'all' ? officeFilter : undefined,
        search: search.trim().length >= 2 ? search.trim() : undefined,
        limit: 500,
        offset: 0,
      });

      setClients(result.data.clients);
      setTotal(result.data.total);
    } catch (err: any) {
      console.error('[BulkActivationPage] Failed to fetch:', err);
      setError(err?.message || 'Failed to load eligible clients.');
    } finally {
      setLoading(false);
    }
  }, [companyId, officeFilter, search]);

  useEffect(() => {
    const timeout = setTimeout(fetchClients, 500);
    return () => clearTimeout(timeout);
  }, [fetchClients]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
    setSelected(new Set());
  }, [officeFilter, search, hideEnrolled]);

  // Filtered + paginated
  const filteredClients = useMemo(() => {
    let result = clients;
    if (hideEnrolled) {
      result = result.filter((c) => !c.isEnrolled);
    }
    return result;
  }, [clients, hideEnrolled]);

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const paginatedClients = filteredClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Unique brokers from current data for display
  const brokerOptions = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((c) => {
      if (c.brokerId && c.brokerDisplay) map.set(c.brokerId, c.brokerDisplay);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  // Selection helpers
  const toggleSelect = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredClients.filter((c) => !c.isEnrolled).length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredClients.filter((c) => !c.isEnrolled).map((c) => c.customerName)));
    }
  };

  // CSV parsing
  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

      // Skip header if present
      const startIdx = lines[0]?.toLowerCase().includes('email') || lines[0]?.toLowerCase().includes('name') ? 1 : 0;

      const rows: CsvRow[] = [];
      for (let i = startIdx; i < lines.length; i++) {
        const parts = lines[i].split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
        if (parts.length >= 2) {
          rows.push({
            customerName: parts[0],
            email: parts[1],
          });
        }
      }

      setCsvData(rows);
      setShowCsvModal(true);
    };
    reader.readAsText(file);

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Bulk invite
  const handleBulkInvite = async () => {
    if (!companyId) return;

    // Build client list from selected names + CSV emails
    const emailMap = new Map<string, string>();
    csvData.forEach((row) => {
      emailMap.set(row.customerName.toLowerCase().trim(), row.email);
    });

    const clientsToInvite: Array<{ email: string; displayName: string; customerNames: string[]; backdatePeriod: string }> = [];
    const missingEmails: string[] = [];

    selected.forEach((name) => {
      const email = emailMap.get(name.toLowerCase().trim());
      if (email) {
        clientsToInvite.push({
          email,
          displayName: name,
          customerNames: [name],
          backdatePeriod,
        });
      } else {
        missingEmails.push(name);
      }
    });

    if (missingEmails.length > 0 && clientsToInvite.length === 0) {
      setInviteResult({
        type: 'error',
        text: `No email addresses found for ${missingEmails.length} selected client(s). Upload a CSV with customer_name,email columns first.`,
      });
      return;
    }

    if (clientsToInvite.length === 0) {
      setInviteResult({ type: 'error', text: 'No clients to invite. Select clients and upload a CSV with their email addresses.' });
      return;
    }

    setInviting(true);
    setInviteResult(null);

    try {
      // Batch in groups of 50 (API limit)
      let totalInvited = 0;
      const allErrors: Array<{ email: string; error: string }> = [];

      for (let i = 0; i < clientsToInvite.length; i += 50) {
        const batch = clientsToInvite.slice(i, i + 50);

        const inviteFn = httpsCallable<
          { companyId: string; clients: typeof batch },
          { invited: number; errors: Array<{ email: string; error: string }> }
        >(functions, 'inviteLoyaltyClients');

        const result = await inviteFn({ companyId, clients: batch });
        totalInvited += result.data.invited;
        allErrors.push(...result.data.errors);
      }

      const messages: string[] = [];
      if (totalInvited > 0) messages.push(`${totalInvited} client(s) invited successfully`);
      if (allErrors.length > 0) messages.push(`${allErrors.length} failed: ${allErrors.slice(0, 3).map((e) => e.error).join(', ')}`);
      if (missingEmails.length > 0) messages.push(`${missingEmails.length} skipped (no email)`);

      setInviteResult({
        type: allErrors.length > 0 ? 'error' : 'success',
        text: messages.join('. '),
      });

      if (totalInvited > 0) {
        setSelected(new Set());
        fetchClients();
      }
    } catch (err: any) {
      console.error('[BulkActivationPage] Bulk invite failed:', err);
      setInviteResult({ type: 'error', text: err?.message || 'Bulk invite failed.' });
    } finally {
      setInviting(false);
    }
  };

  const notEnrolledCount = filteredClients.filter((c) => !c.isEnrolled).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UsersRound}
        title="Bulk Activation"
        subtitle="Activate borrowers into the loyalty rewards program"
        onRefresh={fetchClients}
        isRefreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors cursor-pointer">
              <Upload className="w-4 h-4" />
              Upload CSV
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={handleCsvUpload}
              />
            </label>
          </div>
        }
      />

      {/* CSV status bar */}
      {csvData.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <FileSpreadsheet className="w-5 h-5 text-blue-400" />
          <span className="text-sm text-blue-300">
            <strong>{csvData.length}</strong> email mappings loaded from CSV
          </span>
          <button
            onClick={() => setCsvData([])}
            className="ml-auto text-xs text-gray-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search customer names..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        {allOffices.length > 1 && (
          <select
            value={officeFilter}
            onChange={(e) => setOfficeFilter(e.target.value)}
            className="px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Offices</option>
            {allOffices.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        )}
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-sm text-gray-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={hideEnrolled}
            onChange={(e) => setHideEnrolled(e.target.checked)}
            className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
          />
          Hide enrolled
        </label>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl">
          <span className="text-sm text-white font-medium">
            {selected.size} selected
          </span>
          <select
            value={backdatePeriod}
            onChange={(e) => setBackdatePeriod(e.target.value as any)}
            className="px-3 py-1.5 rounded-md bg-gray-700 border border-gray-600 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="0">No backdate</option>
            <option value="3">Backdate 3 months</option>
            <option value="6">Backdate 6 months</option>
            <option value="12">Backdate 12 months</option>
            <option value="fy">Current FY</option>
            <option value="all">All time</option>
          </select>
          <button
            onClick={handleBulkInvite}
            disabled={inviting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed ml-auto"
          >
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UsersRound className="w-4 h-4" />}
            {inviting ? 'Inviting...' : `Activate ${selected.size} Client${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Invite result */}
      {inviteResult && (
        <div className={`p-3 rounded-lg text-sm ${inviteResult.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-500/30' : 'bg-red-900/50 text-red-300 border border-red-500/30'}`}>
          {inviteResult.text}
        </div>
      )}

      {/* Loading State */}
      {loading && clients.length === 0 && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-12 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          <p className="text-sm text-gray-400">Loading eligible clients from commission data...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-gray-800/50 border border-red-500/30 rounded-xl p-8 flex flex-col items-center justify-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <p className="text-sm text-red-300">{error}</p>
          <button onClick={fetchClients} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors mt-2">
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-700/50 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              {filteredClients.length} borrower{filteredClients.length !== 1 ? 's' : ''}
              {hideEnrolled && clients.length !== filteredClients.length && (
                <span className="text-gray-500"> ({clients.length - filteredClients.length} enrolled, hidden)</span>
              )}
            </p>
            {csvData.length === 0 && selected.size > 0 && (
              <p className="text-xs text-yellow-400">Upload a CSV with customer_name,email to activate selected clients</p>
            )}
          </div>

          {filteredClients.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <UsersRound className="w-8 h-8 mx-auto text-gray-600 mb-2" />
              <p>No eligible clients found</p>
              <p className="text-xs text-gray-500 mt-1">All customers may already be enrolled, or try adjusting filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-400">
                <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                  <tr>
                    <th scope="col" className="px-4 py-3 w-10">
                      <button onClick={toggleSelectAll} className="text-gray-400 hover:text-white transition-colors">
                        {selected.size === notEnrolledCount && notEnrolledCount > 0 ? (
                          <CheckSquare className="w-4 h-4 text-blue-400" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th scope="col" className="px-4 py-3">Customer Name</th>
                    <th scope="col" className="px-4 py-3 text-right">Loans</th>
                    <th scope="col" className="px-4 py-3 text-right">Total Trail</th>
                    <th scope="col" className="px-4 py-3 text-right">Total Upfront</th>
                    <th scope="col" className="px-4 py-3">Office</th>
                    <th scope="col" className="px-4 py-3">Broker</th>
                    <th scope="col" className="px-4 py-3">Last Activity</th>
                    <th scope="col" className="px-4 py-3">Email</th>
                    <th scope="col" className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((client) => {
                    const csvEmail = csvData.find((r) => r.customerName.toLowerCase().trim() === client.customerName.toLowerCase().trim())?.email;
                    const isSelected = selected.has(client.customerName);

                    return (
                      <tr
                        key={client.customerName}
                        className={`border-b border-gray-700/50 transition-colors ${isSelected ? 'bg-blue-500/5' : 'hover:bg-gray-700/30'} ${client.isEnrolled ? 'opacity-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          {!client.isEnrolled && (
                            <button onClick={() => toggleSelect(client.customerName)} className="text-gray-400 hover:text-white transition-colors">
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4 text-blue-400" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-white">{client.customerName}</td>
                        <td className="px-4 py-3 text-right">{client.loanCount}</td>
                        <td className="px-4 py-3 text-right font-medium text-white">
                          ${(client.totalTrailCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0 })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          ${(client.totalUpfrontCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0 })}
                        </td>
                        <td className="px-4 py-3">{client.officeName || '--'}</td>
                        <td className="px-4 py-3">{client.brokerDisplay || '--'}</td>
                        <td className="px-4 py-3">{client.latestPeriodMonth || '--'}</td>
                        <td className="px-4 py-3">
                          {csvEmail ? (
                            <span className="text-green-400 text-xs">{csvEmail}</span>
                          ) : (
                            <span className="text-gray-600 text-xs">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {client.isEnrolled ? (
                            <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                              ENROLLED
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
                              NOT ENROLLED
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-700/50">
              <p className="text-sm text-gray-400">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredClients.length)} of {filteredClients.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md border border-gray-600/50 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-400">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md border border-gray-600/50 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CSV Preview Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCsvModal(false)} />
          <div className="relative w-full max-w-lg bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-lg font-semibold text-white">CSV Preview</h3>
              <button onClick={() => setShowCsvModal(false)} className="text-gray-400 hover:text-white transition-colors text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-400">{csvData.length} rows loaded. Format: <code className="text-xs bg-gray-700 px-1 py-0.5 rounded">customer_name, email</code></p>
              <div className="max-h-60 overflow-y-auto bg-gray-700/50 rounded-lg border border-gray-600/50">
                <table className="w-full text-xs">
                  <thead className="text-gray-400 uppercase bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Customer Name</th>
                      <th className="px-3 py-2 text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 20).map((row, i) => (
                      <tr key={i} className="border-t border-gray-600/30">
                        <td className="px-3 py-1.5 text-white">{row.customerName}</td>
                        <td className="px-3 py-1.5 text-gray-300">{row.email}</td>
                      </tr>
                    ))}
                    {csvData.length > 20 && (
                      <tr><td colSpan={2} className="px-3 py-2 text-center text-gray-500">...and {csvData.length - 20} more</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setCsvData([]); setShowCsvModal(false); }} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors">
                  Discard
                </button>
                <button onClick={() => setShowCsvModal(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                  Use {csvData.length} Mappings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActivationPage;
